// src/components/EditProductPage.tsx
import React, { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Trash2 } from 'lucide-react';

// URL Config
const SPRING_API_URL = 'http://localhost:2027';
const NODE_API_URL = 'http://localhost:2026';

// Category Type Definition
interface Category {
  id: number;
  name: string;
  subCategories?: Category[];
}

const EditProductPage: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    
    // --- States ---
    const [productName, setProductName] = useState('');
    const [description, setDescription] = useState('');
    const [price, setPrice] = useState('');
    const [minQuantity, setMinQuantity] = useState('');
    const [unit, setUnit] = useState('');
    const [hsnSku, setHsnSku] = useState('');
    const [youtubeUrl, setYoutubeUrl] = useState('');
    
    // Category Logic (Complex part)
    const [nestedCategories, setNestedCategories] = useState<Category[]>([]);
    // Ye wo array hai jo dropdowns ko control karega (e.g. [1, 5, 44])
    const [selectedCategoryPath, setSelectedCategoryPath] = useState<number[]>([]);
    
    // Image Handling
    const [existingImages, setExistingImages] = useState<any[]>([]);
    const [newImages, setNewImages] = useState<File[]>([]);
    const [newPreviews, setNewPreviews] = useState<string[]>([]);
    
    const [loading, setLoading] = useState(true);

    // --- HELPER: Find Path to Category ID ---
    // Ye function tree me dhoondhta hai ki ID kahan hai aur uska rasta return karta hai
    const findCategoryPath = (categories: Category[], targetId: number, currentPath: number[] = []): number[] | null => {
        for (const cat of categories) {
            // Agar mil gaya, to path return karo
            if (cat.id === targetId) {
                return [...currentPath, cat.id];
            }
            // Agar subcategories hain, to andar dhoondo
            if (cat.subCategories && cat.subCategories.length > 0) {
                const path = findCategoryPath(cat.subCategories, targetId, [...currentPath, cat.id]);
                if (path) return path;
            }
        }
        return null;
    };

    // --- 1. LOAD DATA ---
    useEffect(() => {
        const loadData = async () => {
            const token = localStorage.getItem('sellerToken');
            try {
                // A. Fetch Categories Tree (Node.js)
                const catRes = await fetch(`${NODE_API_URL}/api/categories`);
                const catTree: Category[] = await catRes.json();
                setNestedCategories(catTree);

                // B. Fetch Product Details (Spring Boot)
                const prodRes = await fetch(`${SPRING_API_URL}/api/seller/products/${id}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                
                if (prodRes.ok) {
                    const data = await prodRes.json();
                    
                    // Basic Fields Set karo
                    setProductName(data.productName);
                    setDescription(data.description);
                    setPrice(data.price);
                    setMinQuantity(data.minimumOrderQuantity);
                    setUnit(data.unit);
                    setHsnSku(data.hsnSku || '');
                    setYoutubeUrl(data.youtubeUrl || '');
                    setExistingImages(data.images || []);

                    // --- CATEGORY MAGIC ---
                    // Agar product ke paas category ID hai, to uska path dhoondo
                    if (data.category && data.category.id) {
                        const foundPath = findCategoryPath(catTree, data.category.id);
                        if (foundPath) {
                            setSelectedCategoryPath(foundPath);
                        }
                    }
                }
            } catch (err) { 
                console.error(err); 
                alert("Error loading data");
            } finally { 
                setLoading(false); 
            }
        };
        loadData();
    }, [id]);

    // --- 2. HANDLERS ---
    
    // Handle Category Dropdown Changes
    const handleCategoryChange = (e: ChangeEvent<HTMLSelectElement>, level: number) => {
        const catId = parseInt(e.target.value);
        // Naya path banao: Current level tak ka slice lo aur naya ID jodo
        const newPath = selectedCategoryPath.slice(0, level);
        if (catId) newPath.push(catId);
        setSelectedCategoryPath(newPath);
    };

    const handleNewFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const files = Array.from(e.target.files);
            setNewImages(files);
            setNewPreviews(files.map(f => URL.createObjectURL(f)));
        }
    };

    const deleteExistingImage = async (imageId: number) => {
        if(!window.confirm("Remove this image?")) return;
        const token = localStorage.getItem('sellerToken');
        try {
             await fetch(`${SPRING_API_URL}/api/seller/products/images/${imageId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
             });
             setExistingImages(existingImages.filter(img => img.id !== imageId));
        } catch(e) { alert("Error removing image"); }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        const token = localStorage.getItem('sellerToken');
        
        // Validation: Category select honi chahiye
        if (selectedCategoryPath.length === 0) {
            alert('Please select a category.');
            setLoading(false);
            return;
        }

        // Aakhri selected ID hi main category ID hai
        const finalCategoryId = selectedCategoryPath[selectedCategoryPath.length - 1];
        
        const formData = new FormData();
        formData.append('productName', productName);
        formData.append('description', description);
        formData.append('price', price);
        formData.append('minimumOrderQuantity', minQuantity);
        formData.append('unit', unit);
        formData.append('hsnSku', hsnSku);
        formData.append('categoryId', finalCategoryId.toString()); // Updated Category
        formData.append('youtubeUrl', youtubeUrl);

        // Append NEW images
        newImages.forEach(file => formData.append('imageFiles', file));

        try {
            const res = await fetch(`${SPRING_API_URL}/api/seller/products/${id}`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });

            if (res.ok) {
                alert("Product Updated Successfully!");
                navigate('/seller/product');
            } else {
                alert("Update failed");
            }
        } catch (err) { alert("Error updating"); } 
        finally { setLoading(false); }
    };

    // --- RENDER DYNAMIC CATEGORY DROPDOWNS ---
    let currentCats = nestedCategories;
    const categoryDropDowns = [];
    
    // Loop chalao path ki length tak + 1 (agle level ke liye)
    for (let i = 0; i <= selectedCategoryPath.length; i++) {
        // Agar subcategories khatam ho gayi to loop roko
        if (!currentCats || currentCats.length === 0) break;
        
        const val = selectedCategoryPath[i] || ''; // Current selected value for this level
        
        categoryDropDowns.push(
            <div key={i} className="mb-4">
                <label className="block text-sm font-medium text-gray-700">
                    {i === 0 ? 'Main Category' : `Sub Category Level ${i}`}
                </label>
                <select
                    value={val}
                    onChange={(e) => handleCategoryChange(e, i)}
                    className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                >
                    <option value="">Select Category</option>
                    {currentCats.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                </select>
            </div>
        );

        // Agle loop ke liye 'currentCats' ko update karo
        if (val) {
            const selectedCat = currentCats.find(c => c.id === val);
            currentCats = selectedCat?.subCategories || [];
        } else {
            break;
        }
    }

    if (loading) return <div className="p-10 text-center">Loading product details...</div>;

    return (
        <div className="max-w-4xl mx-auto p-6 bg-white shadow-lg mt-10 rounded">
            <h1 className="text-2xl font-bold mb-6 text-gray-800">Edit Product</h1>
            <form onSubmit={handleSubmit} className="space-y-6">
                
                {/* Basic Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Product Name</label>
                        <input className="mt-1 block w-full border border-gray-300 p-2 rounded" 
                            value={productName} onChange={e => setProductName(e.target.value)} required />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Price (INR)</label>
                        <input className="mt-1 block w-full border border-gray-300 p-2 rounded" 
                            type="number" value={price} onChange={e => setPrice(e.target.value)} required />
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <textarea className="mt-1 w-full border border-gray-300 p-2 rounded" 
                        rows={4} value={description} onChange={e => setDescription(e.target.value)} required />
                </div>

                {/* Inventory */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                         <label className="block text-sm font-medium text-gray-700">Min Order Qty</label>
                         <input className="mt-1 block w-full border border-gray-300 p-2 rounded" 
                            value={minQuantity} onChange={e => setMinQuantity(e.target.value)} required />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Unit</label>
                        <input className="mt-1 block w-full border border-gray-300 p-2 rounded" 
                            value={unit} onChange={e => setUnit(e.target.value)} required />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">HSN/SKU</label>
                        <input className="mt-1 block w-full border border-gray-300 p-2 rounded" 
                            value={hsnSku} onChange={e => setHsnSku(e.target.value)} />
                    </div>
                </div>

                {/* --- DYNAMIC CATEGORIES SECTION --- */}
                <div className="bg-gray-50 p-4 rounded border">
                    <h3 className="text-lg font-medium mb-2 text-blue-800">Category Classification</h3>
                    {categoryDropDowns}
                </div>

                {/* --- IMAGE MANAGEMENT --- */}
                <div className="border p-4 rounded">
                    <label className="font-bold block mb-4">Product Images</label>
                    
                    {/* Existing Images */}
                    {existingImages.length > 0 && (
                        <div className="mb-4">
                            <p className="text-sm text-gray-500 mb-2">Current Images:</p>
                            <div className="flex gap-4 flex-wrap">
                                {existingImages.map((img: any) => (
                                    <div key={img.id} className="relative group">
                                        <img src={img.imageUrl} className="h-24 w-24 object-cover rounded border" alt="product" />
                                        <button type="button" onClick={() => deleteExistingImage(img.id)} 
                                            className="absolute top-0 right-0 bg-red-600 text-white p-1 rounded-full text-xs shadow-md opacity-0 group-hover:opacity-100 transition-opacity">
                                            <Trash2 size={12}/>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* New Images */}
                    <div>
                        <p className="text-sm text-gray-500 mb-2">Upload New Images:</p>
                        <input type="file" multiple accept="image/*" onChange={handleNewFiles} className="mb-2 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"/>
                        <div className="flex gap-2 mt-2">
                            {newPreviews.map((src, i) => (
                                <img key={i} src={src} className="h-16 w-16 object-cover rounded border border-blue-200" alt="preview" />
                            ))}
                        </div>
                    </div>
                </div>

                <button type="submit" disabled={loading} className="w-full bg-blue-600 text-white py-3 rounded font-bold hover:bg-blue-700 transition shadow-lg">
                    {loading ? 'Saving Changes...' : 'Update Product'}
                </button>
            </form>
        </div>
    );
};

export default EditProductPage;